import {computed, DestroyRef, inject, Injectable, signal} from "@angular/core"
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop"
import {IsDefined, IsNotUndefined} from "@cm/utils/filter"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {sortOrganizationsDetailsWithNameAlphabetically} from "@common/helpers/utils/organization"
import {AuthService} from "@common/services/auth/auth.service"
import {SiloService} from "@common/services/auth/silo.service"
import {AllOrganizationsGQL, BasicOrganizationInfoFragment, OrganizationMembershipsGQL} from "@common/services/organizations/organizations.generated"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {StateLabel} from "@labels"
import {BehaviorSubject, combineLatest, distinctUntilChanged, filter, firstValueFrom, switchMap} from "rxjs"

/*
 * Provides access to organizations and organization memberships
 *
 * Use observables to subscribe to all changes,
 * signals to get the currently loaded value immediately
 * (both may be undefined during initial app load and during login),
 * and promises to get the value once the organizations have been loaded
 */
@Injectable({
    providedIn: "root",
})
export class OrganizationsService {
    readonly silo = inject(SiloService)

    // all organizations visible to the user
    readonly $all = signal<BasicOrganizationInfoFragment[] | undefined>(undefined)
    all$ = toObservable(this.$all)
    get all() {
        return firstValueFrom(this.all$.pipe(filter(IsNotUndefined)))
    }

    // organizations that the user is a member of
    readonly $own = signal<BasicOrganizationInfoFragment[] | undefined>(undefined)
    own$ = toObservable(this.$own)
    get own() {
        return firstValueFrom(this.own$.pipe(filter(IsNotUndefined)))
    }

    readonly $ownIds = computed(() => this.$own()?.map((organization) => organization.id) ?? undefined)
    ownIds$ = toObservable(this.$ownIds)
    get ownIds() {
        return firstValueFrom(this.ownIds$.pipe(filter((value) => value !== undefined)))
    }

    // the user's current organization
    readonly $current = computed(() => this.silo.$silo()?.organization)
    current$ = toObservable(this.$current)
    get current() {
        return this.$current()
    }

    readonly $editOptions = computed<StateLabel<string>[]>(() => {
        return (
            (this.auth.$user()?.isStaff ? this.$all() : this.$own())
                ?.filter((organization) => organization.isActive && organization.name)
                ?.map((organization) => ({
                    label: organization.name!,
                    state: organization.id,
                })) ?? []
        )
    })

    readonly $filterOptions = computed<StateLabel<string>[]>(
        () =>
            (this.auth.$user()?.isStaff ? this.$all() : this.$own())
                ?.filter((organization) => organization.visibleInFilters && organization.isActive && organization.name)
                ?.map((organization) => ({
                    label: organization.name!,
                    state: organization.id,
                })) ?? [],
    )
    readonly $ownOptions = computed(() => {
        return (
            this.$own()?.map((organization) => ({
                label: organization.name,
                state: organization.id,
            })) ?? []
        )
    })
    $userIsMemberOf = (organizationId: string | null | undefined) =>
        computed(() => {
            return !!organizationId && this.$ownIds()?.includes(organizationId)
        })

    public initialLoadCompleted$ = new BehaviorSubject<boolean>(false)
    public readonly $initialLoadCompleted = toSignal(this.initialLoadCompleted$)

    readonly auth = inject(AuthService)
    private readonly destroyRef = inject(DestroyRef)
    readonly refresh = inject(RefreshService)

    private readonly organizationMemberships = inject(OrganizationMembershipsGQL)
    private readonly allOrganizationsGql = inject(AllOrganizationsGQL)

    constructor() {
        combineLatest([this.auth.userId$, this.silo.silo$])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                distinctUntilChanged(),
                switchMap(async ([userId, silo]) => {
                    switch (userId) {
                        case null: {
                            return {
                                all: [],
                                own: [],
                                current: null,
                            }
                        }
                        case undefined: {
                            return {
                                all: undefined,
                                own: undefined,
                                current: undefined,
                            }
                        }
                        default: {
                            const {user} = await fetchThrowingErrors(this.organizationMemberships)({userId})
                            const batchSize = 100
                            let allOrganizations: BasicOrganizationInfoFragment[] = []
                            for (let i = 0; i < 100; i++) {
                                const {organizations} = await fetchThrowingErrors(this.allOrganizationsGql)(
                                    {
                                        skip: batchSize * i,
                                        take: batchSize,
                                    },
                                    {context: {withoutSilo: true}},
                                )
                                allOrganizations = [...allOrganizations, ...organizations.filter(IsDefined)]
                                if (organizations.length === 0) {
                                    break
                                }
                            }
                            const siloOrganizationId = silo?.organization?.id
                            if (siloOrganizationId) {
                                const ownOrganizations = allOrganizations.filter((organization) => organization.id === siloOrganizationId)
                                return {
                                    all: allOrganizations,
                                    own: ownOrganizations,
                                }
                            } else {
                                const ownOrganizations = allOrganizations.filter((organization) =>
                                    user.memberships?.some((membership) => membership.organization?.id === organization.id),
                                )
                                return {
                                    all: allOrganizations,
                                    own: ownOrganizations,
                                }
                            }
                        }
                    }
                }),
            )
            .subscribe(({all, own}) => {
                this.$all.set(all ? sortOrganizationsDetailsWithNameAlphabetically(all) : all)
                this.$own.set(own ? sortOrganizationsDetailsWithNameAlphabetically(own) : own)
                this.initialLoadCompleted$.next(true)
            })
    }

    userIsMemberOf(organizationId: string | null | undefined) {
        return (!!organizationId && this.$own()?.some((organization) => organization.id === organizationId)) ?? false
    }

    async byId(organizationId: string | null) {
        return this.all.then((all) => all?.find((organization) => organization.id === organizationId) ?? null)
    }

    $byId(organizationId: string | null) {
        return computed(() => this.$all()?.find((organization) => organization.id === organizationId) ?? null)
    }

    /**
     * Return the provided organizationId if the user is a member, otherwise default to one the user is a member of.
     * @param organizationId
     * @return Promise<string> The id of an organization the current user is a member of.
     */
    async ensureUserIsMember(organizationId?: string | null): Promise<string> {
        await firstValueFrom(this.initialLoadCompleted$.pipe(filter((value) => !!value)))
        if (organizationId && this.userIsMemberOf(organizationId)) {
            return organizationId
        }
        const fallbackId = this.$current()?.id ?? this.$own()?.[0]?.id
        if (!fallbackId) {
            throw new Error("No fallback organization found")
        }
        return fallbackId
    }
}
